import React, { useEffect, useState } from 'react'
import { configRepository, initBranch, State } from '../../const'
import * as lodash from 'lodash'
import {
  getAvailableTag,
  getBranches,
  getDeploymentTag,
  getFeaturesJSON,
  getLatestTags,
  getPipelineState,
  getPipelineStateOfHEAD,
  getPipelineSteps,
  isHeadTagged,
  setTag,
  startDeploymentPipeline,
  updateConfig
} from '../../Controller'
import {
  APIResponse,
  RepositoryDeploymentProperties,
  DeploymentOperation,
  DeploymentProperties,
  PipelineState,
  Result,
  Selector,
  TagResponse,
  StatusLine,
  FeatureJsonComponent
} from '../../types'
import { DeploymentUI } from './ui/DeploymentUI'

export function Deployment() {
  const checkIntervalActive = 15000 // During Deployment Process - 15sec
  const checkIntervalPassive = 90000 // During Browsing - 90sec
  const [deploymentTag, setDeploymentTag] = useState('')
  const [instance, setInstance] = useState(initBranch)
  const [toDeploy, setToDeploy] = useState([] as DeploymentProperties[])
  const [pipelineStates, setPipelineStates] = useState([] as PipelineState[])
  const [repositories, setRepositories] = useState([] as RepositoryDeploymentProperties[])
  const [pipelineSteps, setPipelineSteps] = useState([] as Selector[])
  const [deploymentStatus, setDeploymentStatus] = useState({
    message: '',
    fetching: false,
    failed: false
  } as StatusLine)
  const [observePipeline, setObservePipeline] = useState(true)

  useEffect(() => {
    init(initBranch)
  }, [])

  useEffect(() => {
    let timers = [] as NodeJS.Timer[]
    if (observePipeline) {
      timers = toDeploy.map((component: DeploymentProperties, index: number) => {
        return setInterval(async () => {
          const state = await getPipelineStateOfHEAD(component.repository, component.branch)
          setPipelineStates(prevState => {
            if (prevState[index]) {
              if (prevState[index] !== state.result) {
                const newState = lodash.cloneDeep(prevState)
                newState[index] = state.result
                return newState
              }
            }
            return prevState
          })
        }, checkIntervalPassive)
      })
    }
    return () => {
      timers.forEach(interval => {
        clearInterval(interval)
      })
    }
  }, [toDeploy, observePipeline])

  //initialization

  async function init(branch: string) {
    const featureJson = await getFeaturesJSON(branch)
    if (featureJson.success) {
      const repositoryRequest = getRepositories(featureJson.result.components)
      const deploymentRequest = getDeploymentInformation(featureJson.result.components)
      const tagRequest = getDeploymentTag(configRepository, featureJson.result.system)
      const pipelineStepsRequest = getPipelineSteps(initBranch)
      const responses = await Promise.all([repositoryRequest, deploymentRequest, tagRequest, pipelineStepsRequest])
      const repositoryInformation = responses[0]
      const deploymentInformation = responses[1]
      const newDeploymentTag = responses[2].success ? responses[2].result : ''
      const newPipelineSteps = responses[3].success ? responses[3].result : ([] as Selector[])

      const pipelineStateRequests = deploymentInformation.map((component: DeploymentProperties) =>
        getPipelineStateOfHEAD(component.repository, component.branch)
      )
      const pipelineStateResponses = await Promise.all(pipelineStateRequests)
      const newPipelineStates: PipelineState[] = pipelineStateResponses.map(response =>
        response.success ? response.result : State.notFound
      )
      setInstance(branch)
      setPipelineStates(newPipelineStates)
      setPipelineSteps(newPipelineSteps)
      setDeploymentTag(newDeploymentTag)
      setToDeploy(deploymentInformation)
      setRepositories(repositoryInformation)
    } else {
      init(initBranch)
      setDeploymentStatus({ message: 'Cant find feature.json for ' + branch, fetching: false, failed: true })
    }
  }

  async function getRepositories(components: FeatureJsonComponent[]): Promise<RepositoryDeploymentProperties[]> {
    let newState: RepositoryDeploymentProperties[] = components.map(component => {
      return {
        name: component.name,
        repository: component.repository,
        lastTag: component.tag,
        branches: []
      }
    })
    const branchesRequests: Promise<APIResponse | Result<string[]>>[] = newState.map(component =>
      getBranches(component.repository)
    )
    const branchesResponses = await Promise.all(branchesRequests)
    newState = newState.map((component, index) => {
      if (branchesResponses[index].success) {
        return {
          ...component,
          branches: branchesResponses[index].result
        }
      }
      return component
    })
    return newState
  }

  async function getDeploymentInformation(components: FeatureJsonComponent[]): Promise<DeploymentProperties[]> {
    let newState: DeploymentProperties[] = components.map(component => {
      return {
        branch: component.branch,
        name: component.name,
        repository: component.repository,
        upToDate: false,
        tag: component.tag,
        tags: []
      }
    })
    const latestTagRequests: Promise<APIResponse | Result<TagResponse[]>>[] = newState.map(component =>
      getLatestTags(component.repository, component.branch)
    )
    const latestTagResponses = await Promise.all(latestTagRequests)
    newState = newState.map((component, index) => {
      if (latestTagResponses[index].success && latestTagResponses[index].result.length > 0) {
        component.tag = latestTagResponses[index].result[0].tag
        component.tags = latestTagResponses[index].result
      }
      return component
    })
    const upToDateRequests = newState.map(component =>
      isHeadTagged(component.repository, component.branch, component.tags)
    )
    const upToDateResponses = await Promise.all(upToDateRequests)
    newState = newState.map((component, index) => {
      component.upToDate =
        upToDateResponses[index] && upToDateResponses[index].success ? upToDateResponses[index].result : false
      return component
    })
    return newState
  }

  //change states

  async function changeDeploy(operation: DeploymentOperation, index?: number, value?: string) {
    switch (operation) {
      case 'GET_NEW_TAG':
        await getNewTag(index)
        break
      case 'CHANGE_TAG':
        if (value && index !== undefined) {
          changeTag(value, index)
        }
        break
      case 'CHANGE_BRANCH':
        if (value && index !== undefined) {
          await changeBranch(value, index)
        }
        break
      default:
        break
    }
    return true
  }

  function getValue(response: APIResponse | Result<any>, defaultValue: any) {
    return response && response.success ? response.result : defaultValue
  }

  async function changeBranch(branch: string, index: number) {
    const newPipelineStates = lodash.cloneDeep(pipelineStates)
    const newToDeploy = lodash.cloneDeep(toDeploy)
    newToDeploy[index].branch = branch

    const tags = getLatestTags(newToDeploy[index].repository, newToDeploy[index].branch)
    const upToDate = isHeadTagged(newToDeploy[index].repository, newToDeploy[index].branch, newToDeploy[index].tags)
    const pipeline = getPipelineStateOfHEAD(newToDeploy[index].repository, branch)
    const responses = await Promise.all([tags, upToDate, pipeline])

    newToDeploy[index].tags = getValue(responses[0], toDeploy[index].tags)
    newToDeploy[index].upToDate = getValue(responses[1], toDeploy[index].upToDate)
    newPipelineStates[index] = getValue(responses[2], pipelineStates[index])
    if (responses[0].result.length > 0) {
      newToDeploy[index].tag = newToDeploy[index].tags[0].tag
    } else {
      const response = await getAvailableTag(newToDeploy[index].repository, newToDeploy[index].branch)
      if (response.success) {
        newToDeploy[index].tag = response.result
        newToDeploy[index].upToDate = true
      }
    }
    setToDeploy(newToDeploy)
    setPipelineStates(newPipelineStates)
    return true
  }

  function changeTag(value: string, index: number) {
    const newToDeploy = lodash.cloneDeep(toDeploy)
    newToDeploy[index].tag = value
    setToDeploy(newToDeploy)
  }

  //no index gets new tag for every repository
  async function getNewTag(index?: number) {
    const newState = lodash.cloneDeep(toDeploy)
    //0 === false
    if (index !== undefined) {
      const response = await getAvailableTag(newState[index].repository, newState[index].branch)
      if (response && response.success) {
        newState[index].tag = response.result
        newState[index].upToDate = true
      }
    } else {
      const tagRequests = toDeploy.map(information => {
        return information.upToDate ? undefined : getAvailableTag(information.repository, information.branch)
      })
      const tagResponses = await Promise.all(tagRequests)
      tagResponses.forEach((response, index) => {
        if (response && response.success) {
          newState[index].tag = response.result
          newState[index].upToDate = true
        }
      })
    }
    setToDeploy(newState)
    return true
  }

  //handle deployment

  async function setTags(selector?: Selector) {
    setObservePipeline(false)
    setDeploymentStatus({ message: 'Setting tags...', fetching: true, failed: false })
    const requests: Promise<APIResponse | Result<TagResponse>>[] = []
    toDeploy.forEach((component: DeploymentProperties, index: number) => {
      if (isNewTag(index)) {
        requests.push(setTag(component.repository, component.branch, component.tag))
      }
    })
    const responses = await Promise.all(requests)
    const allSuccessful = responses.every(result => result.success)
    if (allSuccessful) {
      const updateConfigResponse = await updateConfig(instance, deploymentTag, toDeploy)
      if (updateConfigResponse.success) {
        requests.length > 0 ? checkPipeline(responses as Result<TagResponse>[], selector) : startDeployment(selector)
      } else if (updateConfigResponse.code === 500) {
        setDeploymentStatus({ message: updateConfigResponse.result, failed: true, fetching: false })
      } else {
        setDeploymentStatus({ message: 'Failed to update Feature.json!', failed: true, fetching: false })
      }
    } else {
      setDeploymentStatus({ message: 'Failed to set tags!', failed: true, fetching: false })
    }
  }

  async function checkPipeline(setTagResponses: Result<TagResponse>[], selector?: Selector) {
    setDeploymentStatus({ message: 'Running Pipelines...', failed: false, fetching: true })
    const requests = toDeploy.map((component: DeploymentProperties, index: number) => {
      if (isNewTag(index)) {
        const response = setTagResponses.find(
          (response: Result<TagResponse>) => response.result.repository === component.repository
        ) as Result<TagResponse>
        return getPipelineState(component.repository, response.result.commit)
      }
    })
    const responses = await Promise.all(requests)
    const currentStates = responses.map(response =>
      response && response.success ? response.result : State.notFound
    ) as PipelineState[]
    const newStates: PipelineState[] = responses.map((response, index) =>
      response && response.success ? response.result : pipelineStates[index]
    )
    setPipelineStates(newStates)
    checkIfCompleted(currentStates, setTagResponses, selector)
  }

  function checkIfCompleted(
    currentStates: PipelineState[],
    setTagResponses: Result<TagResponse>[],
    selector?: Selector
  ) {
    const allDone = currentStates.every((state: PipelineState) => {
      return state !== State.running && state !== State.pending
    })
    // check if all pipelines are complete and successful (or not found - no pipeline to run)
    if (allDone) {
      const allSuccessful = toDeploy.every((component: DeploymentProperties, index: number) => {
        return isNewTag(index)
          ? currentStates[index] === State.successful || currentStates[index] === State.notFound
          : true
      })
      allSuccessful
        ? startDeployment(selector)
        : setDeploymentStatus({ message: 'Pipeline failed!', failed: true, fetching: false })
    } else {
      // check again in interval
      setTimeout(() => checkPipeline(setTagResponses, selector), checkIntervalActive)
    }
  }

  async function startDeployment(selector?: Selector) {
    if (selector) {
      const responseStartPipeline = await startDeploymentPipeline(instance, selector)
      if (responseStartPipeline.success) {
        const checkState = setInterval(async () => {
          const state = await getPipelineState(configRepository, responseStartPipeline.result)
          if (state.result !== State.running && state.result !== State.pending) {
            state.result === State.successful
              ? setDeploymentStatus({ message: 'Deployment complete!', failed: false, fetching: false })
              : setDeploymentStatus({ message: 'Deployment Pipeline failed!', failed: true, fetching: false })
            clearInterval(checkState)
          }
        }, checkIntervalActive)
      } else {
        setDeploymentStatus({ message: 'Failed to start Deployment Pipeline!', failed: true, fetching: false })
      }
    } else {
      setDeploymentStatus({ message: 'Deployment complete!', failed: false, fetching: false })
    }
  }

  function isNewTag(index: number) {
    const find = toDeploy[index].tags.find(tagObj => tagObj.tag === toDeploy[index].tag)
    return find ? false : true
  }

  return (
    <DeploymentUI
      repositories={repositories}
      deploymentTag={deploymentTag}
      deploymentStatus={deploymentStatus}
      toDeploy={toDeploy}
      pipelineStates={pipelineStates}
      pipelineSteps={pipelineSteps}
      instance={instance}
      onInstanceChange={init}
      onDeployChange={changeDeploy}
      onStartDeployment={setTags}
    />
  )
}

export default Deployment
